import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Clear from "@mui/icons-material/Clear";
import Logo from "assets/images/logo.ico";
import { apiPost } from "lib/api";
import { useGlobalMDAlert } from "context";
import {
  useMaterialUIController,
  setNombrenegocio,
  setAccesoweb,
  setRoleweb,
  setRoleDetalles,
  setModulos,
  setUsuarioLog,
  setClaveLog,
  setAnchoCodigoBarra,
  setAltoCodigoBarra,
} from "context";

const NAVY = "#0B1F33";
/** Botón principal (azul). */
const BLUE = "#2563EB";
const BLUE_HOVER = "#1D4ED8";
/** Panel lateral claro (contraste con texto oscuro). */
const PANEL = "#D8E3EE";
const LINE = "#E5E7EB";

export default function Login() {
  const [usuario, setUsuario] = useState("");
  const [clave, setClave] = useState("");
  const [verPass, setVerPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [, dispatch] = useMaterialUIController();
  const navigate = useNavigate();
  const refUsuario = useRef(null);
  const { showAlert, setShowAlert } = useGlobalMDAlert();

  function handleLogin() {
    setError("");
    if (!String(usuario).trim()) {
      refUsuario.current?.focus();
      return;
    }
    if (!String(clave).trim()) {
      setError("Ingrese su contraseña.");
      return;
    }
    if (loading) return;

    setLoading(true);
    apiPost("/api/loginbackend_buscardatosiniciales", { usuario, password: clave })
      .then(({ status, data }) => {
        if (status === 200) {
          setClaveLog(dispatch, clave);
          setUsuarioLog(dispatch, usuario);
          setNombrenegocio(dispatch, data.nombrenegocio[0].ncomp);
          setAnchoCodigoBarra(dispatch, Number(data.nombrenegocio[0].anchocodigobarra) || 0);
          setAltoCodigoBarra(dispatch, Number(data.nombrenegocio[0].altocodigobarra) || 0);
          setAccesoweb(dispatch, data.usuarios[0].web);
          setRoleweb(dispatch, data.usuarios[0].tipo);
          setRoleDetalles(dispatch, data.roles);
          setModulos(dispatch, data.modulos);
          localStorage.setItem("id", data.id);
          localStorage.setItem("usuario", "");
          localStorage.setItem("password", "");
          localStorage.setItem("roleweb", "");
          localStorage.setItem("sucursal", data.usuarios[0].sucursal);
          localStorage.setItem("nombrenegocio", data.nombrenegocio[0].ncomp);
          localStorage.setItem("sucursalmatricula", data.nombrenegocio[0].sucursalmatricula);
          localStorage.setItem("anchocodigobarra", Number(data.nombrenegocio[0].anchocodigobarra) || 0);
          localStorage.setItem("altocodigobarra", Number(data.nombrenegocio[0].altocodigobarra) || 0);
          const web = data.usuarios[0].web;
          if (web === "S" || web === "s") {
            navigate("/moduloscliente");
          } else {
            showAlert;
          }
        }
        if (status === 400) {
          showAlert({ message: `Error: ${data.Error}`, color: "error", autoHideDuration: 3000 });
        }
      })
      .catch((err) => showAlert({ message: err?.message || "Error de conexión", color: "error", autoHideDuration: 3000 }))
      .finally(() => setLoading(false));
  }

  const inputSx = {
    mb: 2.5,
    "& .MuiOutlinedInput-root": {
      borderRadius: 1,
      bgcolor: "#FFFFFF",
      fontSize: { xs: "1.0625rem", md: "1.125rem" },
      py: 0.25,
      "& fieldset": { borderColor: LINE },
      "&:hover fieldset": { borderColor: "#D1D5DB" },
      "&.Mui-focused fieldset": { borderColor: NAVY },
    },
    "& .MuiInputLabel-root": { color: "#64748B", fontSize: { xs: "0.9375rem", md: "1rem" } },
  };

  return (
    <Box
      sx={{
        minHeight: { xs: "100dvh", md: "100vh" },
        width: "100%",
        maxWidth: "100vw",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        bgcolor: "#F4F5F7",
        overflowX: "hidden",
        pt: { xs: "env(safe-area-inset-top)", md: 0 },
        pb: { xs: "env(safe-area-inset-bottom)", md: 0 },
        pl: { xs: "env(safe-area-inset-left)", md: 0 },
        pr: { xs: "env(safe-area-inset-right)", md: 0 },
      }}
    >
      {/* Panel institucional */}
      <Box
        sx={{
          bgcolor: PANEL,
          px: { xs: 3, sm: 4, md: 7 },
          py: { xs: 3, sm: 4, md: 10 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          width: { md: "44%", lg: "40%" },
          flexShrink: 0,
          minHeight: { xs: "auto", md: "min(100dvh, 100vh)" },
          borderRight: { md: `1px solid rgba(15, 23, 42, 0.08)` },
        }}
      >
        <Box>
          <Box
            component="img"
            src={Logo}
            alt=""
            sx={{ height: { xs: 48, md: 52 }, width: "auto", mb: { xs: 2.5, md: 4 } }}
          />
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { xs: "1.25rem", sm: "1.35rem", md: "1.5rem" },
              letterSpacing: "-0.02em",
              lineHeight: 1.35,
              mb: 1.5,
              maxWidth: { xs: "100%", sm: 380 },
              color: NAVY,
            }}
          >
            Portal de gestión empresarial
          </Typography>
          <Typography
            sx={{
              color: "#475569",
              fontSize: { xs: "0.9375rem", md: "1rem" },
              lineHeight: 1.65,
              maxWidth: { xs: "100%", sm: 360 },
            }}
          >
            Acceso autorizado para personal registrado. Las credenciales son personales e intransferibles.
          </Typography>
        </Box>
        <Typography sx={{ display: { xs: "none", md: "block" }, color: "#64748B", fontSize: "0.75rem", mt: 4 }}>
          © {new Date().getFullYear()} · Uso interno
        </Typography>
      </Box>

      {/* Formulario */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 2.5, sm: 4, md: 7 },
          width: "100%",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 600, md: 680, lg: 720 },
            bgcolor: "#FFFFFF",
            border: `1px solid ${LINE}`,
            borderRadius: { xs: 2, md: 2.5 },
            p: { xs: 4, sm: 5.5, md: 7 },
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: NAVY,
              mb: 1,
              fontSize: { xs: "1.45rem", sm: "1.55rem", md: "1.7rem" },
            }}
          >
            Inicio de sesión
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#64748B", mb: 3.5, lineHeight: 1.6, fontSize: { xs: "1rem", md: "1.0625rem" }, fontWeight: 600 }}
          >
            Introduzca usuario y contraseña.
          </Typography>

          <TextField
            inputRef={refUsuario}
            label="Usuario"
            fullWidth
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            variant="outlined"
            disabled={loading}
            autoComplete="username"
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
            InputProps={{
              endAdornment:
                String(usuario).length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label="Limpiar usuario"
                      onClick={() => {
                        setUsuario("");
                        setError("");
                        refUsuario.current?.focus();
                      }}
                      sx={{ color: "#94A3B8" }}
                    >
                      <Clear sx={{ fontSize: 20 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
            }}
          />

          <TextField
            label="Contraseña"
            fullWidth
            type={verPass ? "text" : "password"}
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            variant="outlined"
            disabled={loading}
            autoComplete="current-password"
            InputLabelProps={{ shrink: true }}
            sx={{ ...inputSx, mb: error ? 1 : 2 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleLogin();
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => setVerPass(!verPass)}
                    aria-label={verPass ? "Ocultar contraseña" : "Mostrar contraseña"}
                    sx={{ color: "#94A3B8" }}
                  >
                    {verPass ? <VisibilityOff sx={{ fontSize: 22 }} /> : <Visibility sx={{ fontSize: 22 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error ? (
            <Typography sx={{ color: "#B42318", fontSize: "0.8125rem", mb: 2 }}>{error}</Typography>
          ) : null}

          <Button
            fullWidth
            variant="contained"
            disableElevation
            disabled={loading}
            onClick={handleLogin}
            sx={{
              py: { xs: 1.5, md: 1.65 },
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "1.0625rem", md: "1.125rem" },
              color: "#FFFFFF",
              bgcolor: BLUE,
              "&:hover": { bgcolor: BLUE_HOVER, color: "#FFFFFF" },
              "&.Mui-disabled": { color: "#FFFFFF", bgcolor: BLUE, opacity: 0.85 },
            }}
          >
            {loading ? (
              <CircularProgress size={26} thickness={4} sx={{ color: "#FFFFFF" }} aria-label="Iniciando sesión" />
            ) : (
              "Continuar"
            )}
          </Button>

          <Typography variant="body2" sx={{ display: "block", textAlign: "center", mt: 3, color: "red", fontSize: "0.875rem" }}>
            Si olvidó sus datos, contacte al administrador del sistema.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
